import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SnoozeIcon from '@mui/icons-material/Snooze';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import BlockIcon from '@mui/icons-material/Block';
import SuperAdminService, {
  MutedRemindersResult,
  SuperAdminReminder,
} from '../../services/SuperAdminService';
import { REMINDER_KIND_TEXTS } from '../../contexts/SuperAdminRemindersContext';

/**
 * Hatırlatma susturma pencereleri (zil satır menüsü, bu dönem için kapat,
 * şirket takibini bırak, susturulanlar listesi).
 */

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '');

const errorText = (e: any) => e?.message || 'İşlem yapılamadı';

// ───────────────────────── Satır menüsü ─────────────────────────

interface ReminderActionsMenuProps {
  anchorEl: HTMLElement | null;
  reminder: SuperAdminReminder | null;
  onClose: () => void;
  onGo: (reminder: SuperAdminReminder) => void;
  /** Ertele / dönem kapat / takibi bırak tamamlandığında (liste yenilenir). */
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}

export const ReminderActionsMenu: React.FC<ReminderActionsMenuProps> = ({ anchorEl, reminder, onClose, onGo, onChanged, onError }) => {
  const [periodOpen, setPeriodOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [target, setTarget] = useState<SuperAdminReminder | null>(null);

  const snooze = async (days: number) => {
    if (!reminder) return;
    const r = reminder;
    onClose();
    try {
      await SuperAdminService.getInstance().dismissReminder(r.key, days, null);
      onChanged(`${r.title} — ${days} gün ertelendi`);
    } catch (e) {
      onError(errorText(e));
    }
  };

  const goLabel = reminder ? (REMINDER_KIND_TEXTS[reminder.kind]?.goLabel ?? 'Git') : 'Git';

  return (
    <>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl) && !!reminder} onClose={onClose}>
        <MenuItem
          onClick={() => {
            if (reminder) onGo(reminder);
            onClose();
          }}
        >
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{goLabel}</ListItemText>
        </MenuItem>
        <Divider />
        <ListSubheader sx={{ lineHeight: '28px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Ertele
        </ListSubheader>
        <MenuItem onClick={() => snooze(7)}>
          <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>7 gün ertele</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => snooze(30)}>
          <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>30 gün ertele</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setTarget(reminder);
            setPeriodOpen(true);
            onClose();
          }}
        >
          <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Bu dönem için kapat…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTarget(reminder);
            setCompanyOpen(true);
            onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Şirketin takibini bırak…</ListItemText>
        </MenuItem>
      </Menu>

      <DismissPeriodDialog
        open={periodOpen}
        reminder={target}
        onClose={() => setPeriodOpen(false)}
        onDone={(msg) => {
          setPeriodOpen(false);
          onChanged(msg);
        }}
      />
      <CompanyReminderMuteDialog
        open={companyOpen}
        companyId={target?.companyId ?? null}
        companyName={target?.title ?? ''}
        onClose={() => setCompanyOpen(false)}
        onDone={(msg) => {
          setCompanyOpen(false);
          onChanged(msg);
        }}
      />
    </>
  );
};

// ───────────────────────── Bu dönem için kapat ─────────────────────────

interface DismissPeriodDialogProps {
  open: boolean;
  reminder: SuperAdminReminder | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

export const DismissPeriodDialog: React.FC<DismissPeriodDialogProps> = ({ open, reminder, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote('');
      setError(null);
    }
  }, [open]);

  if (!reminder) return null;
  const kindName = REMINDER_KIND_TEXTS[reminder.kind]?.name ?? reminder.kind;
  const expiry = fmtDate(reminder.expiryDate);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await SuperAdminService.getInstance().dismissReminder(reminder.key, null, note.trim() || null);
      onDone(`${reminder.title} — uyarı bu dönem için kapatıldı`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Uyarı bu dönem için kapatılsın mı?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {reminder.title} · {kindName}{expiry ? ` · Bitiş ${expiry}` : ''}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Bu uyarı {expiry ? `${expiry} bitiş tarihi için` : 'bu dönem için'} bir daha çıkmaz. Sözleşme yenilenip yeni bir bitiş
          tarihi girilirse yeni dönem için yeniden hatırlatılır.
        </Alert>
        <TextField
          label="Not (isteğe bağlı)"
          fullWidth
          size="small"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>Vazgeç</Button>
        <Button variant="contained" onClick={submit} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ───────────────────────── Şirket takibini bırak ─────────────────────────

interface CompanyReminderMuteDialogProps {
  open: boolean;
  companyId: string | null;
  companyName: string;
  onClose: () => void;
  onDone: (message: string) => void;
}

export const CompanyReminderMuteDialog: React.FC<CompanyReminderMuteDialogProps> = ({ open, companyId, companyName, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (!companyId) return;
    if (!reason.trim()) {
      setError('Neden yazın.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await SuperAdminService.getInstance().muteCompanyReminders(companyId, reason.trim());
      onDone(`${companyName} — takip bırakıldı`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{companyName} için takip bırakılsın mı?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 1 }}>Bu şirket için şunlar durur:</Typography>
        <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 2.5, typography: 'body2', color: 'text.secondary' }}>
          <li>Zil ve uyarı bandındaki tüm hatırlatmalar (sözleşme, ürün, demo, yazarkasa)</li>
          <li>Yenileme e-postaları</li>
        </Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Şirketin girişi, verisi ve açık tahsilatları değişmez. İstediğinizde şirket menüsünden takibi yeniden açabilirsiniz.
        </Alert>
        <TextField
          label="Neden"
          required
          fullWidth
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Örn. Sözleşme yenilenmedi, çalışmıyoruz"
          inputProps={{ maxLength: 500 }}
          autoFocus
        />
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={saving}>Vazgeç</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={saving || !reason.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Takibi bırak
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ───────────────────────── Susturulanlar ─────────────────────────

interface MutedRemindersDialogProps {
  open: boolean;
  onClose: () => void;
  /** Bir kayıt geri açıldığında (liste ve zil yenilenir). */
  onChanged: (message: string) => void;
}

export const MutedRemindersDialog: React.FC<MutedRemindersDialogProps> = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState<MutedRemindersResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await SuperAdminService.getInstance().getMutedReminders());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const restore = async (id: number, title: string) => {
    setBusy(`d:${id}`);
    try {
      await SuperAdminService.getInstance().restoreReminder(id);
      onChanged(`${title} — uyarı geri açıldı`);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const unmute = async (companyId: string, name: string) => {
    setBusy(`c:${companyId}`);
    try {
      await SuperAdminService.getInstance().unmuteCompanyReminders(companyId);
      onChanged(`${name} — takip yeniden açıldı`);
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const empty = !loading && data && data.dismissed.length === 0 && data.companies.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Susturulanlar</DialogTitle>
      <DialogContent dividers>
        {loading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {empty && <Typography color="text.secondary">Susturulmuş uyarı yok.</Typography>}

        {data && data.dismissed.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">Ertelenen / kapatılan uyarılar</Typography>
            {data.dismissed.map((d) => (
              <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {d.title} · {REMINDER_KIND_TEXTS[d.kind]?.name ?? d.kind}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {d.snoozedUntil
                      ? `${fmtDate(d.snoozedUntil)} tarihine kadar ertelendi`
                      : `Bu dönem için kapatıldı${d.expiryDate ? ` (bitiş ${fmtDate(d.expiryDate)})` : ''}`}
                    {d.createdBy ? ` · ${d.createdBy}` : ''}
                    {d.reason ? ` · "${d.reason}"` : ''}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" disabled={busy === `d:${d.id}`} onClick={() => restore(d.id, d.title)}>
                  Geri aç
                </Button>
              </Box>
            ))}
          </Box>
        )}

        {data && data.companies.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">Takip dışı şirketler</Typography>
            {data.companies.map((c) => (
              <Box key={c.companyId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {c.reason ? `"${c.reason}"` : 'Neden yok'}
                    {c.mutedBy ? ` · ${c.mutedBy}` : ''}
                    {c.mutedAt ? ` · ${fmtDate(c.mutedAt)}` : ''}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" disabled={busy === `c:${c.companyId}`} onClick={() => unmute(c.companyId, c.name)}>
                  Takibi aç
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};
